import React, { useMemo, useState } from "react";
import { ZoomIn, ZoomOut, Snowflake } from "lucide-react";
import { slotToLabel, absenceCellsForDay } from "../lib/time";

const ROW_H = 42;
const LABEL_W = 216;
const ZOOM_LEVELS = [8, 11, 14, 20, 28];

function buildSegments(timeline) {
  const segs = [];
  let i = 0;
  while (i < timeline.length) {
    const cur = timeline[i];
    let j = i + 1;
    while (j < timeline.length && timeline[j].type === cur.type && timeline[j].ws === cur.ws) j++;
    segs.push({ start: i, len: j - i, ...cur });
    i = j;
  }
  return segs;
}

export function GanttTimeline({ config, agents, workstations, schedule, absences, professions, day }) {
  const [zoom, setZoom] = useState(2);
  const CELL_W = ZOOM_LEVELS[zoom];
  const N = config.slots_per_day;
  const dateStr = config.days[day].date;

  const wsById = useMemo(() => Object.fromEntries(workstations.map((w) => [w.id, w])), [workstations]);
  const absMap = useMemo(() => absenceCellsForDay(config, absences, dateStr), [config, absences, dateStr]);
  const profName = useMemo(() => Object.fromEntries(professions.map((p) => [p.id, p.name])), [professions]);

  const assignByAgent = useMemo(() => {
    const m = {};
    (schedule?.assignments || []).filter((a) => a.day === day).forEach((a) => {
      (m[a.agent_id] = m[a.agent_id] || {})[a.slot] = a;
    });
    return m;
  }, [schedule, day]);

  const hourTicks = [];
  for (let t = 0; t < N; t++) if ((config.day_start_min + t * config.slot_min) % 60 === 0) hourTicks.push(t);

  const freezeGlobal = schedule?.freeze_until_global || 0;
  const freezeInDay = freezeGlobal - day * N;
  const wholeDayFrozen = freezeGlobal >= (day + 1) * N;
  const lunchStart = config.lunch_start_slot;
  const lunchEnd = config.lunch_end_slot;

  return (
    <div>
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          <span className="head text-sm font-semibold text-ink">{config.days[day].label} · {dateStr}</span>
          {wholeDayFrozen && (
            <span className="mono text-[10px] px-2 py-0.5 rounded-sm bg-primary/15 text-primary border border-primary/30 inline-flex items-center gap-1">
              <Snowflake className="w-3 h-3" /> FROZEN
            </span>
          )}
        </div>
        <div className="flex items-center gap-1">
          <button data-testid="gantt-zoom-out" onClick={() => setZoom((z) => Math.max(0, z - 1))}
            className="w-8 h-8 flex items-center justify-center rounded-sm border border-line text-muted hover:text-ink hover:border-lineFocus transition-colors">
            <ZoomOut className="w-4 h-4" />
          </button>
          <span className="mono text-[10px] text-faint w-8 text-center">{CELL_W}px</span>
          <button data-testid="gantt-zoom-in" onClick={() => setZoom((z) => Math.min(ZOOM_LEVELS.length - 1, z + 1))}
            className="w-8 h-8 flex items-center justify-center rounded-sm border border-line text-muted hover:text-ink hover:border-lineFocus transition-colors">
            <ZoomIn className="w-4 h-4" />
          </button>
        </div>
      </div>

      <div className="overflow-auto border border-line rounded-sm bg-surface" data-testid="gantt-timeline">
        <div style={{ minWidth: LABEL_W + N * CELL_W }}>
          <div className="flex sticky top-0 z-20 bg-surface2 border-b border-line">
            <div className="sticky left-0 z-30 bg-surface2 border-r border-line flex items-center px-3" style={{ width: LABEL_W, height: 32 }}>
              <span className="mono text-[10px] uppercase tracking-widest text-muted">Agent / Time</span>
            </div>
            <div className="relative" style={{ width: N * CELL_W, height: 32 }}>
              {hourTicks.map((t) => (
                <div key={t} className="absolute top-0 h-full flex items-center border-l border-line" style={{ left: t * CELL_W }}>
                  <span className="mono text-[10px] text-muted pl-1">{slotToLabel(config, t)}</span>
                </div>
              ))}
            </div>
          </div>

          {agents.map((agent) => {
            const timeline = new Array(N).fill(null).map(() => ({ type: "off", ws: null }));
            (absMap[agent.id] || new Set()).forEach((t) => (timeline[t] = { type: "absent", ws: null }));
            const rowAssign = assignByAgent[agent.id] || {};
            Object.values(rowAssign).forEach((a) => {
              timeline[a.slot] = a.state === "break" ? { type: "break", ws: null } : { type: "work", ws: a.workstation_id };
            });
            const segs = buildSegments(timeline);
            const bounds = schedule?.shift_bounds?.[agent.id]?.[String(day)];
            const shiftLabel = bounds && bounds.end_slot > bounds.start_slot
              ? `${slotToLabel(config, bounds.start_slot)}–${slotToLabel(config, bounds.end_slot)}`
              : "Rest day";

            return (
              <div key={agent.id} className="flex border-b border-line group" data-testid={`gantt-row-${agent.id}`}>
                <div className="sticky left-0 z-10 bg-surface group-hover:bg-surface2 border-r border-line flex items-center gap-2.5 px-3 transition-colors duration-150" style={{ width: LABEL_W, height: ROW_H }}>
                  {agent.avatar_url ? (
                    <img src={agent.avatar_url} alt="" className="w-7 h-7 rounded-sm object-cover border border-line" />
                  ) : <div className="w-7 h-7 rounded-sm bg-surface2 border border-line" />}
                  <div className="min-w-0">
                    <div className="text-xs font-medium text-ink truncate">{agent.name}</div>
                    <div className="mono text-[10px] text-faint truncate">{profName[agent.profession_id] || agent.profession_id} · {shiftLabel}</div>
                  </div>
                </div>

                <div className="relative" style={{
                  width: N * CELL_W, height: ROW_H,
                  backgroundImage: `repeating-linear-gradient(90deg, transparent 0, transparent ${CELL_W - 1}px, #1c1c1e ${CELL_W - 1}px, #1c1c1e ${CELL_W}px)`,
                }}>
                  <div className="absolute top-0 h-full bg-warning/[0.04] border-x border-warning/10" style={{ left: lunchStart * CELL_W, width: (lunchEnd - lunchStart) * CELL_W }} />
                  {wholeDayFrozen && <div className="absolute inset-0 bg-primary/[0.05]" />}
                  {freezeInDay > 0 && freezeInDay <= N && (
                    <div data-testid={`freeze-marker-${agent.id}`} className="absolute top-0 h-full w-[2px] bg-primary z-10 shadow-[0_0_6px_rgba(10,132,255,0.8)]" style={{ left: freezeInDay * CELL_W }} title={`Frozen up to ${slotToLabel(config, freezeInDay)}`} />
                  )}
                  {segs.map((s) => {
                    if (s.type === "off") return null;
                    const w = wsById[s.ws];
                    const width = s.len * CELL_W - 2;
                    const label = s.type === "break" ? "BREAK" : s.type === "absent" ? "ABSENT" : (w?.name || "");
                    const bg = s.type === "work" ? (w?.color || "#0A84FF") : s.type === "break" ? "#2A2A2C" : "transparent";
                    return (
                      <div key={s.start} data-testid={`gantt-seg-${agent.id}-${s.start}`}
                        title={`${label} · ${slotToLabel(config, s.start)}–${slotToLabel(config, s.start + s.len)}`}
                        className={`absolute top-1 rounded-sm overflow-hidden flex items-center px-1.5 ${s.type === "absent" ? "diag-hatch border border-critical/50" : "border border-black/20"} ${s.type === "break" ? "border-dashed border-white/15" : ""}`}
                        style={{ left: s.start * CELL_W + 1, width, height: ROW_H - 8, backgroundColor: bg }}>
                        {width > 34 && (
                          <span className={`mono text-[9px] font-medium truncate ${s.type === "break" ? "text-muted" : s.type === "absent" ? "text-critical" : "text-white"}`}
                            style={{ textShadow: s.type === "work" ? "0 1px 1px rgba(0,0,0,0.4)" : "none" }}>{label}</span>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
